import { SetStateAction } from 'react';
import Media from 'react-media';

import { FacetItem, ProductDetail } from '@api/catalog';

import ProductFilter, { Category } from '@components/Filters';
import Select from '@components/controls/Select';
import Switcher from '@components/controls/Switcher';

import { VIEW_OPTIONS } from '@scripts/enums/products';
import { Button, colors, scale, useTheme } from '@scripts/gds';
import { useMedia } from '@scripts/hooks';

import LinesIcon from '@icons/24/lines.svg';
import SmallLinesIcon from '@icons/24/small-lines.svg';
import SquareIcon from '@icons/24/square.svg';

import { SortFilter } from './types';

const sortOptions = [
    { value: 'price', label: 'Сначала дешевые' },
    //todo исправить value когда появится соотв фильтр Сначала популярные
    { value: 'id', label: 'Сначала популярные' },
];

const ControlViewItems = ({
    filter,
    setFilter,
    activeView,
    setActiveView,
    categories,
    filterFields,
    priceRange,
    preExpandedFilters,
    setProductDetailsData,
}: {
    filter: SortFilter;
    setFilter: (val: SetStateAction<SortFilter>) => void;
    activeView: string;
    setActiveView: (val: string) => void;
    categories?: Category[];
    filterFields: FacetItem[];
    priceRange?: { max: number; min: number };
    preExpandedFilters?: any;
    setProductDetailsData?: (arg: ProductDetail[]) => void;
}) => {
    const getOptionByValue = (value: string) => sortOptions.find(option => option.value === value);
    const { md, mdMin } = useMedia();
    const { layout } = useTheme();

    return (
        <>
            {filterFields.length !== 0 && (
                <div
                    css={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                        marginRight: scale(1),
                    }}
                >
                    <Switcher
                        name="isPreOrder"
                        onClick={(e: any) => {
                            setProductDetailsData && setProductDetailsData([]);
                            setFilter(prev => ({
                                ...prev,
                                isPreOrder: Boolean(e.target?.checked),
                            }));
                        }}
                        defaultChecked={filter.isPreOrder}
                    >
                        В наличии
                    </Switcher>
                    <Switcher
                        name="isSale"
                        onClick={(e: any) => {
                            setProductDetailsData && setProductDetailsData([]);
                            setFilter(prev => ({
                                ...prev,
                                isSale: Boolean(e.target?.checked),
                            }));
                        }}
                        defaultChecked={filter.isSale}
                    >
                        Товары со скидкой
                    </Switcher>
                </div>
            )}

            <div
                css={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    marginLeft: scale(3),
                    [md]: {
                        width: '100%',
                        justifyContent: 'space-between',
                        marginLeft: 0,
                    },
                }}
            >
                <Select
                    simple
                    label="Сортировать"
                    items={sortOptions}
                    defaultIndex={0}
                    selectedItem={getOptionByValue(filter.sort)}
                    defaultValue={filter.sort}
                    onChange={e => {
                        setProductDetailsData && setProductDetailsData([]);
                        setFilter({ ...filter, sort: String(e.selectedItem?.value) || '' });
                    }}
                    css={{
                        width: scale(32),
                        marginRight: scale(3),
                        [md]: {
                            display: 'none',
                        },
                    }}
                />

                <Media query={{ minWidth: layout?.breakpoints.md || 1024 }} defaultMatches={false}>
                    {matches =>
                        matches ? (
                            <Button
                                data-testid="button-square"
                                theme="fillLightGrey"
                                css={{
                                    height: scale(6),
                                    borderRadius: '6px 0 0 6px !important',
                                    '> svg': {
                                        fill:
                                            activeView === VIEW_OPTIONS.DEFAULT
                                                ? colors.textMainBlack
                                                : colors.textNuanceLight,
                                    },
                                }}
                                onClick={() => setActiveView(VIEW_OPTIONS.DEFAULT)}
                                aria-label={VIEW_OPTIONS.DEFAULT}
                            >
                                <SquareIcon />
                            </Button>
                        ) : (
                            <Button
                                data-testid="button-square"
                                theme="fillLightGrey"
                                css={{
                                    borderRadius: '6px 0 0 6px !important',
                                    '> svg': { fill: colors.textMainBlack },
                                    border: 'none !important',
                                    padding: `0px ${scale(5, true)}px 0px 0px !important`,
                                    height: scale(4),
                                    background: '#fff !important',
                                }}
                                onClick={() =>
                                    setActiveView(
                                        activeView === VIEW_OPTIONS.DEFAULT ? VIEW_OPTIONS.LINES : VIEW_OPTIONS.DEFAULT
                                    )
                                }
                                aria-label={
                                    activeView === VIEW_OPTIONS.DEFAULT ? VIEW_OPTIONS.LINES : VIEW_OPTIONS.DEFAULT
                                }
                            >
                                {activeView === VIEW_OPTIONS.DEFAULT ? <SquareIcon /> : <LinesIcon />}
                            </Button>
                        )
                    }
                </Media>

                <Select
                    simple
                    label="Сортировать"
                    items={sortOptions}
                    defaultIndex={0}
                    selectedItem={getOptionByValue(filter.sort)}
                    defaultValue={filter.sort}
                    onChange={e => {
                        setProductDetailsData && setProductDetailsData([]);
                        setFilter({ ...filter, sort: String(e.selectedItem?.value) || '' });
                    }}
                    css={{
                        width: scale(32),
                        marginRight: scale(1),
                        [mdMin]: {
                            display: 'none',
                        },
                    }}
                />

                <Button
                    data-testid="button-line"
                    theme="fillLightGrey"
                    css={{
                        height: scale(6),
                        borderRadius: '0 !important',
                        '> svg': {
                            fill: activeView === VIEW_OPTIONS.LINES ? colors.textMainBlack : colors.textNuanceLight,
                        },
                        [md]: {
                            display: 'none !important',
                        },
                    }}
                    onClick={() => setActiveView(VIEW_OPTIONS.LINES)}
                    aria-label={VIEW_OPTIONS.LINES}
                >
                    <LinesIcon />
                </Button>

                <Button
                    data-testid="button-small-line"
                    theme="fillLightGrey"
                    css={{
                        height: scale(6),
                        borderRadius: '0 6px 6px 0 !important',
                        '> svg': {
                            fill:
                                activeView === VIEW_OPTIONS.SMALL_LINES ? colors.textMainBlack : colors.textNuanceLight,
                        },
                        [md]: {
                            display: 'none !important',
                        },
                    }}
                    onClick={() => setActiveView(VIEW_OPTIONS.SMALL_LINES)}
                    aria-label={VIEW_OPTIONS.SMALL_LINES}
                >
                    <SmallLinesIcon />
                </Button>

                <Media query={{ maxWidth: layout?.breakpoints.md || 1024 }} defaultMatches={false}>
                    {matches =>
                        matches &&
                        filterFields.length !== 0 && (
                            <div>
                                <ProductFilter
                                    categories={categories}
                                    filterFields={filterFields}
                                    priceRange={priceRange}
                                    fieldsWithSearch={['brand_id']}
                                    preExpandedFilters={preExpandedFilters}
                                    setProductDetailsData={setProductDetailsData}
                                />
                            </div>
                        )
                    }
                </Media>
            </div>
        </>
    );
};

export default ControlViewItems;
